import { AccountType } from "@common/constants/domains/accounting/accounts";
import { AccountRole } from "@common/types/domains/accounting/account";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { EditableAccountPatch } from "../draftState";
import { GetRowCapabilities } from "../editorCapabilities";
import { NestedEditorHost } from "./nestedRowRefusal";

// The (type, parentGroupId) scope a per-row "Add account" / "Add group"
// affordance targets: a leaf tier row scopes to its leaf type with a null
// parent; a custom group row scopes to the group's type + id; an account row
// scopes to its own (type, parentGroupId). `hostRowId` is the tree row id of the
// affordance's host so a newly added child can be revealed by expanding the host
// + its ancestors. `hostAccountId` is set only when the host is an account row,
// so a new account can be inserted immediately after it (group/leaf hosts
// prepend as the first child instead).
export interface NestedAddScope {
    type: AccountType;
    parentGroupId: string | null;
    hostRowId: string;
    hostAccountId: string | null;
    entityIds: number[];
    hostAccountIdByEntityId: Record<number, string>;
    parentGroupIdByEntityId: Record<number, string | null>;
}

export interface NestedEditorCallbacks {
    onUpdateAccount: (id: string, patch: EditableAccountPatch) => void;
    // Renaming an account that holds a role is confirmed first; the editor
    // holds the typed name until the modal is answered.
    onRequestRenameAccount: (params: {
        id: string;
        name: string;
        role: AccountRole;
    }) => void;
    onRenameGroup: (id: string, name: string) => void;
    onAddAccount: (scope: NestedAddScope) => void;
    onAddGroup: (scope: NestedAddScope) => void;
    // Enable (false) / Archive (true) an account from its per-row ⋯ menu.
    onSetDisabled: (id: string, isDisabled: boolean) => void;
    // Open the delete-confirmation modal for a single account.
    onDeleteAccount: (id: string) => void;
    // Open the delete-confirmation modal for a custom group.
    onDeleteGroup: (id: string) => void;
    // A pending Add left its name blank: drop the draft rows it minted.
    onCancelPendingAdd: (draftId: string) => void;
    // Workspace host only: open the Merge / Remove opening balance modal.
    onMergeAccount?: (id: string) => void;
    onRemoveOpeningBalance?: (id: string) => void;
    onReassignRole: (id: string) => void;
}

export interface NestedEditorMeta {
    // Host-supplied per-row capabilities (rename / code-edit / type-change /
    // archive / delete reasons + placeholder presentation).
    getRowCapabilities: GetRowCapabilities;
    // Per-account, not the OR wrapper. Otherwise a clean first constituent is named.
    getConstituentCapabilities: GetRowCapabilities;
    // Ids of account rows whose code duplicates another account's; the code
    // cell renders the red error styling.
    duplicateCodeIds: Set<string>;
    // Ids of custom groups whose name duplicates a sibling in scope; the group
    // name cell renders the red error styling (blank names use the yellow
    // "Add name" affordance via flagWhenEmpty instead).
    duplicateGroupNameIds: Set<string>;
    // Draft ids of accounts whose name collides with a sibling in scope; the
    // name cell renders the red error styling (blank names use flagWhenEmpty).
    duplicateNameIds: Set<string>;
    displayedEntityIds: number[] | undefined;
    // Workspace Enable stays clickable; templates still disable it.
    host: NestedEditorHost;
    // Draft ids of rows minted by an inline Add whose name has not landed yet.
    pendingDraftIds: ReadonlySet<string>;
    // Workspace recovery: the draft stays on screen but takes no further
    // gestures until Retry reseeds from the server.
    isEditingDisabled: boolean;
}

export type NestedEditorTableMeta = ViewTableMeta<
    NestedEditorCallbacks,
    never
> &
    NestedEditorMeta;

const isNestedEditorTableMeta = (
    meta: object | undefined,
): meta is NestedEditorTableMeta =>
    meta !== undefined &&
    "host" in meta &&
    "getRowCapabilities" in meta &&
    typeof meta.getRowCapabilities === "function";

export const getNestedEditorTableMeta = (table: {
    options: { meta?: object };
}): NestedEditorTableMeta | null =>
    isNestedEditorTableMeta(table.options.meta) ? table.options.meta : null;
